/* ============================================================================
   07 · Collection landing & lookbook header
   ----------------------------------------------------------------------------
   Pattern: an editorial header sits above the collection grid. The header
   uses two-depth parallax; the headline is split into lines at runtime with
   SplitType and revealed with the same masked-line pattern the hero uses;
   the grid supports a density toggle that FLIP-animates between layouts;
   the whole page is scrolled through Lenis for the settle-vs-snap difference
   on scrubbed sections.

   What's being decided here:
     • Where the editorial header ends and the grid begins. A hard divider
       reads as "this is the shop, now"; a fade blurs the intent. Currently
       the filter/sort bar reveals on scroll-past the header, which acts as
       the transition — no line, no seam.
     • Depth ratio. Far layer moves at parallax.far, near at parallax.mid.
       The gap between them is the depth. Push both up and the header feels
       stuck; drop both and the header reads flat.
     • Filter reveal. The chip bar slides down (transform, not top change) on
       first entry into the sticky position. This has the same energy as the
       hero's masked line reveal deliberately — a shopper's eye is already
       trained for it.
     • Density toggle. The 4-up ↔ 3-up swap uses GSAP Flip. Each card
       animates from its old rect to its new rect over --mo-slow. Without
       Flip the layout jumps and reads as a new page load.
     • Sort menu. Opens with scale/opacity from its trigger's corner
       (transform-origin: top right). This gives the menu a source, which is
       the Material principle we call out most in review.

   Lenis: mounted opt-in. Reduced motion disables it (falls back to native
   scroll) and skips the parallax entirely.
   ========================================================================= */

import { initMotion, refreshOnSettle, isReduced, MOTION } from '../../src/motion.js';
import { mountLabBar } from '../../src/lab.js';
import { initSmoothScroll } from '../../src/scroll.js';
import { splitLines } from '../../src/split.js';

const { gsap, ScrollTrigger } = initMotion();
mountLabBar({ title: '07 · Collection landing' });
refreshOnSettle();
initSmoothScroll(ScrollTrigger);

/* --- Product catalogue placeholder ---------------------------------------- */
const PALETTES = [
  ['--p-caribbean-blue', '--p-navy'],
  ['--p-mauve', '--p-burgundy'],
  ['--p-hunter-green', '--p-espresso'],
  ['--p-royal-blue', '--p-navy'],
  ['--p-sandstone', '--p-espresso'],
  ['--p-surgical-green', '--p-hunter-green'],
  ['--p-lavender-dew', '--p-mauve'],
  ['--p-ceil-blue', '--p-royal-blue'],
];

const CATALOGUE = [
  { n: 'Catarina One-Pocket Top',   f: 'FIONx', c: 'tops',  p: 58 },
  { n: 'Casma Three-Pocket Top',    f: 'FORMx', c: 'tops',  p: 62 },
  { n: 'Rafaela Oversized Top',     f: 'FIONx', c: 'tops',  p: 66 },
  { n: 'Leon Two-Pocket Top',       f: 'FIONx', c: 'tops',  p: 54 },
  { n: 'Zamora Jogger Scrub Pant',  f: 'FORMx', c: 'pants', p: 68 },
  { n: 'Livingston Basic Pant',     f: 'FIONx', c: 'pants', p: 52 },
  { n: 'Kade Cargo Scrub Pant',     f: 'FORMx', c: 'pants', p: 74 },
  { n: 'Vienna Modern-Fit Top',     f: 'FIONx', c: 'tops',  p: 58 },
  { n: 'Yola Skinny Scrub Pant',    f: 'FORMx', c: 'pants', p: 62 },
  { n: 'Delta Half-Zip Pullover',   f: 'FORMx', c: 'tops',  p: 88 },
  { n: 'Marlowe Zip Jacket',        f: 'FORMx', c: 'tops',  p: 118 },
  { n: 'On-Shift Short Sleeve Tee', f: 'FIONx', c: 'tops',  p: 34 },
];

const grid = document.querySelector('[data-grid]');
let filter = 'all';

function renderGrid() {
  const shown = CATALOGUE.filter((row) => {
    if (filter === 'all') return true;
    if (filter === 'fionx' || filter === 'formx') return row.f.toLowerCase() === filter;
    return row.c === filter;
  });

  grid.innerHTML = shown
    .map((row, i) => {
      const palette = PALETTES[i % PALETTES.length];
      return `
      <article class="card" data-card data-fabric="${row.f}" data-category="${row.c}">
        <div class="card__media">
          <span class="card__badge">${row.f}&trade;</span>
          <div class="card__image" style="background:linear-gradient(155deg, var(${palette[0]}), var(${palette[1]}) 85%)"></div>
          <span class="card__fabric">${row.f}&trade;</span>
        </div>
        <div class="card__info">
          <h2 class="card__name">${row.n}</h2>
          <p class="card__price">$${row.p}</p>
        </div>
      </article>`;
    })
    .join('');
}

renderGrid();

/* --- Editorial header — SplitType + entry --------------------------------- */
function buildHeader() {
  const eyebrow = document.querySelector('.lookbook__eyebrow');
  const lede = document.querySelector('.lookbook__lede');
  const meta = document.querySelector('.lookbook__meta');
  const cue = document.querySelector('.lookbook__scroll-cue');
  const titleEl = document.querySelector('[data-mo-split="lines"]');

  if (isReduced()) {
    gsap.set([eyebrow, lede, meta, cue, titleEl], { opacity: 1, y: 0 });
    return;
  }

  const lines = splitLines(titleEl);

  gsap.set([eyebrow, lede, meta, cue], { opacity: 0, y: MOTION.rise.sm });
  gsap.set(lines, { yPercent: 120 });

  gsap
    .timeline({ defaults: { ease: MOTION.ease.entrance } })
    .to(eyebrow, { opacity: 1, y: 0, duration: MOTION.dur.slow }, 0)
    .to(
      lines,
      {
        yPercent: 0,
        duration: MOTION.dur.deliberate,
        stagger: MOTION.stagger.loose,
      },
      0.15
    )
    .to(lede, { opacity: 1, y: 0, duration: MOTION.dur.slow }, '-=0.45')
    .to(meta, { opacity: 1, y: 0, duration: MOTION.dur.base }, '-=0.35')
    .to(cue, { opacity: 1, y: 0, duration: MOTION.dur.base }, '-=0.25');
}

/* --- Parallax ------------------------------------------------------------- */
function buildParallax() {
  if (isReduced()) return;
  const far = document.querySelector('[data-depth="far"]');
  const near = document.querySelector('[data-depth="near"]');

  gsap.to(far, {
    yPercent: MOTION.parallax.far * 100,
    ease: MOTION.ease.linear,
    scrollTrigger: {
      trigger: '.lookbook',
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    },
  });

  gsap.to(near, {
    yPercent: MOTION.parallax.mid * 100,
    ease: MOTION.ease.linear,
    scrollTrigger: {
      trigger: '.lookbook',
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    },
  });
}

/* --- Sticky filter bar reveal --------------------------------------------- */
function buildFilterReveal() {
  const bar = document.querySelector('[data-filters]');
  if (isReduced()) {
    bar.dataset.revealed = 'true';
    return;
  }
  ScrollTrigger.create({
    trigger: '.lookbook',
    start: 'bottom top+=60',
    onEnter: () => (bar.dataset.revealed = 'true'),
    onLeaveBack: () => (bar.dataset.revealed = 'false'),
  });
}

/* --- Filter chip clicks (Flip re-layout) ---------------------------------- */
document.querySelector('[data-chips]').addEventListener('click', (e) => {
  const chip = e.target.closest('[data-chip]');
  if (!chip) return;
  const next = chip.dataset.chip;
  if (next === filter) return;

  document.querySelectorAll('[data-chip]').forEach((c) => c.classList.remove('chip--active'));
  chip.classList.add('chip--active');
  filter = next;
  transitionGrid(() => renderGrid());
});

/* --- Density toggle (Flip) ------------------------------------------------ */
document.querySelectorAll('[data-density]').forEach((btn) =>
  btn.addEventListener('click', () => {
    const value = btn.dataset.density;
    if (grid.dataset.density === value) return;

    document.querySelectorAll('[data-density]').forEach((b) => b.classList.remove('density__btn--active'));
    btn.classList.add('density__btn--active');

    transitionGrid(() => (grid.dataset.density = value));
  })
);

/**
 * Wrap a DOM mutation in a GSAP Flip capture. Cards animate from their old
 * rect to their new rect over --mo-slow. Under reduced motion the mutation
 * still happens; the animation is skipped so the layout snaps instead.
 */
function transitionGrid(mutation) {
  if (!window.gsap?.Flip || isReduced()) {
    mutation();
    return;
  }
  const cards = grid.querySelectorAll('[data-card]');
  const state = window.gsap.Flip.getState(cards, { props: 'opacity' });
  mutation();
  window.gsap.Flip.from(state, {
    duration: MOTION.dur.slow,
    ease: MOTION.ease.entrance,
    stagger: MOTION.stagger.tight,
    absolute: true,
    onEnter: (elements) =>
      gsap.fromTo(
        elements,
        { opacity: 0, y: MOTION.rise.sm },
        { opacity: 1, y: 0, duration: MOTION.dur.slow, ease: MOTION.ease.entrance }
      ),
    onLeave: (elements) =>
      gsap.to(elements, {
        opacity: 0,
        y: -MOTION.rise.xs,
        duration: MOTION.dur.base,
        ease: MOTION.ease.exit,
      }),
  });
}

/* --- Sort menu ------------------------------------------------------------ */
const sort = document.querySelector('[data-sort]');
const sortTrigger = document.querySelector('[data-sort-trigger]');
const sortMenu = document.querySelector('[data-sort-menu]');
const sortCurrent = document.querySelector('[data-sort-current]');

function openSort() {
  sortTrigger.setAttribute('aria-expanded', 'true');
  sortMenu.hidden = false;
  if (isReduced()) return;
  gsap.fromTo(
    sortMenu,
    { autoAlpha: 0, y: -6, scale: 0.96 },
    {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      duration: MOTION.dur.base,
      ease: MOTION.ease.entrance,
    }
  );
}

function closeSort() {
  sortTrigger.setAttribute('aria-expanded', 'false');
  if (isReduced()) {
    sortMenu.hidden = true;
    return;
  }
  gsap.to(sortMenu, {
    autoAlpha: 0,
    y: -6,
    scale: 0.96,
    duration: MOTION.dur.quick,
    ease: MOTION.ease.exit,
    onComplete: () => (sortMenu.hidden = true),
  });
}

sortTrigger.addEventListener('click', () => {
  if (sortMenu.hidden) openSort();
  else closeSort();
});

sortMenu.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-sort-value]');
  if (!btn) return;
  sortMenu.querySelectorAll('[data-sort-value]').forEach((b) => b.setAttribute('aria-selected', 'false'));
  btn.setAttribute('aria-selected', 'true');

  // Label crossfade so the sort choice reads as an update, not a substitution.
  gsap
    .timeline()
    .to(sortCurrent, { opacity: 0, y: -4, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
    .set(sortCurrent, { textContent: btn.dataset.sortValue })
    .to(sortCurrent, { opacity: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance });

  closeSort();
});

document.addEventListener('click', (e) => {
  if (sortMenu.hidden) return;
  if (!sort.contains(e.target)) closeSort();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeSort();
});

/* --- Rive mount point -----------------------------------------------------
   The fabric badge on each card is where a Rive state-machine would replace
   the static label — swatch morph on hover, "selected" state on tap.
   src/rive.js exposes mountRive(); wire it once a .riv artboard is authored.
   Left as a static badge in this pass so the experiment is honest about
   what's actually shipped. */

/* --- Build --------------------------------------------------------------- */
function build() {
  ScrollTrigger?.getAll().forEach((t) => t.kill());
  renderGrid();
  buildHeader();
  buildParallax();
  buildFilterReveal();
  ScrollTrigger?.refresh();
}

build();
window.addEventListener('mo:replay', build);
